import { homeScore, visitorScore } from './match-details.js';
/**
 * Get current period number
 *
 * @param {Match} match
 * @returns {number}
 */
export function currentPeriodNumber(match) {
    // Check value is between 1 and 999
    if (match.gcCurrentPeriod >= 1 && match.gcCurrentPeriod <= 999) {
        return match.gcCurrentPeriod;
    }
    // Otherwise return 1 (first period)
    return 1;
}
/**
 * Is currently in regular period?
 *
 * @param match
 * @returns {boolean}
 */
export function isCurrentlyInRegularPeriod(match) {
    return match.config.regularPeriods.quantity >= currentPeriodNumber(match);
}
/**
 * Is currently in last regular period?
 *
 * @param match
 * @returns {boolean}
 */
export function isInLastRegularPeriod(match) {
    return match.config.regularPeriods.quantity === currentPeriodNumber(match);
}
/**
 * Is currently in overtime period?
 *
 * @param {Match} match
 * @returns {boolean}
 */
export function isCurrentlyInOvertimePeriod(match) {
    const config = match.config;
    // Guard config for overtime periods not set at all
    if (!config.overtimePeriods) {
        return false;
    }
    // Config for overtime periods is set, but quantity is 0
    if (config.overtimePeriods.quantity === 0) {
        return false;
    }
    // Is currently in regular period
    if (isCurrentlyInRegularPeriod(match)) {
        return false;
    }
    return totalNumberOfPeriods(match) >= currentPeriodNumber(match);
}
/**
 * Total number of periods
 *
 * @param {Match} match
 * @returns {number}
 */
export function totalNumberOfPeriods(match) {
    const config = match.config;
    let output = config.regularPeriods.quantity;
    if (config.overtimePeriods && config.overtimePeriods.quantity) {
        output += config.overtimePeriods.quantity;
    }
    return output;
}
/**
 * Has next period?
 *
 * @param {Match} match
 * @returns {boolean}
 */
export function hasNextPeriod(match) {
    return totalNumberOfPeriods(match) > currentPeriodNumber(match);
}
/**
 * Get duration of a specific period as numeric value
 *
 * @param {Match} match
 * @param {number} periodNumber
 */
export function durationOfSpecificPeriod(match, periodNumber) {
    // Guard parsed period number is below 1
    if (periodNumber < 1) {
        return 0;
    }
    // Guard parsed period number is larger than total number of periods in match
    if (periodNumber > totalNumberOfPeriods(match)) {
        return 0;
    }
    // Regular period
    if (periodNumber <= match.config.regularPeriods.quantity) {
        return match.config.regularPeriods.duration;
    }
    // Overtime period
    if (match.config.overtimePeriods && periodNumber <= match.config.overtimePeriods.quantity) {
        return match.config.overtimePeriods.duration;
    }
    return 0;
}
/**
 * Get duration of current period as numeric value
 *
 * @param {Match} match
 */
export function durationOfCurrentPeriod(match) {
    if (isCurrentlyInRegularPeriod(match)) {
        return match.config.regularPeriods.duration;
    }
    if (isCurrentlyInOvertimePeriod(match) && match.config.overtimePeriods) {
        return match.config.overtimePeriods.duration;
    }
    return 0;
}
/**
 * Is current period allowing extra time
 *
 * @param {Match} match
 * @returns {boolean}
 */
export function isCurrentPeriodAllowingExtraTime(match) {
    if (isCurrentlyInRegularPeriod(match)) {
        return match.config.regularPeriods.allowExtraTime;
    }
    if (isCurrentlyInOvertimePeriod(match) && match.config.overtimePeriods) {
        return match.config.overtimePeriods.allowExtraTime;
    }
    return false;
}
/**
 * Period text
 * E.g.
 * 	'1 / 3'
 * 	'OT'
 *  'OT1'
 * @param {Match} match
 * @returns {string}
 */
export function periodText(match) {
    const currentPeriod = currentPeriodNumber(match);
    if (isCurrentlyInRegularPeriod(match)) {
        return `${currentPeriod} / ${match.config.regularPeriods.quantity}`;
    }
    if (isCurrentlyInOvertimePeriod(match) && match.config.overtimePeriods) {
        if (match.config.overtimePeriods.quantity === 1) {
            return 'OT';
        }
        const overtimePeriodNumber = currentPeriod - match.config.regularPeriods.quantity;
        return `OT${overtimePeriodNumber} / OT${match.config.overtimePeriods.quantity}`;
    }
    return '';
}
/**
 * Get time unboxed numeric value
 *
 * @param {Match} match
 * @param {Timestamp} now
 * @returns {number} unboxed time
 */
export function timeUnboxedNumericValue(match, now) {
    // Guard no last stored value
    if (match.gcLastValue === undefined) {
        return 0;
    }
    // Get latest noted time value
    let time = match.gcLastValue;
    // If gc current started then calculate difference in time and add
    if (match.gcState && match.gcState === 'STARTED' && match.gcLastStarted) {
        const diffInMs = now.toMillis() - match.gcLastStarted.toMillis();
        // Only add diff time is positive
        if (diffInMs > 0) {
            time += Math.floor(diffInMs) / 1000; // Convert to seconds with decimals, e.g. 0.001 for 1/1000 of a second
        }
    }
    return time;
}
/**
 * Get current time in period as numeric value
 *
 * @param {Match} match
 * @param {Timestamp} now
 * @returns {number}
 */
export function timeNumericValue(match, now) {
    const time = timeUnboxedNumericValue(match, now);
    const currentPeriodDuration = durationOfCurrentPeriod(match);
    if (time > currentPeriodDuration) {
        return currentPeriodDuration;
    }
    return time;
}
/**
 * Get extra time as numeric value
 *
 * @param match
 * @param now
 * @returns {number} Returns empty string '' if not allowed in current period. Otherwise returns numeric values as expected
 */
export function extraTimeNumericValue(match, now) {
    // Guard current period not allowing extra time
    if (!isCurrentPeriodAllowingExtraTime(match)) {
        return '';
    }
    const timeUnboxed = timeUnboxedNumericValue(match, now);
    const currentPeriodDuration = durationOfCurrentPeriod(match);
    const extraTimeNumeric = timeUnboxed - currentPeriodDuration;
    if (extraTimeNumeric <= 0.0) {
        return '';
    }
    return extraTimeNumeric;
}
/**
 * Is at end of current period?
 *
 * @param {Match} match
 * @param {Timestamp} now
 * @returns {boolean} Returns true if at end of current period
 */
export function isAtEndOfCurrentPeriod(match, now) {
    // Guard check if current period is allowing extra time
    if (isCurrentPeriodAllowingExtraTime(match)) {
        return false;
    }
    return Math.floor(durationOfCurrentPeriod(match)) === Math.floor(timeNumericValue(match, now));
}
/**
 * Can go to next period?
 *
 * @param {Match} match
 * @param {Timestamp} now
 * @returns {boolean} Returns true if
 * 	- clock is paused
 *  - is at end of current period
 *  - has next period
 */
export function canGotoNextPeriod(match, now) {
    // Must be paused
    if (match.gcState !== 'PAUSED') {
        return false;
    }
    // Must be at end of period
    if (!isAtEndOfCurrentPeriod(match, now)) {
        return false;
    }
    // Must has next period
    if (!hasNextPeriod(match)) {
        return false;
    }
    // Check if is in last regular period and has overtime periods
    // - if thats the case, you can only go to next period (overtime) if the scores are tie
    if (isInLastRegularPeriod(match)
        && match.config.overtimePeriods
        && match.config.overtimePeriods.quantity > 0) {
        return homeScore(match) === visitorScore(match);
    }
    return true;
}
/**
 * Get progress percentage of current period
 *
 * @param {Match} match
 * @param {Timestamp} now
 * @returns {number}
 */
export function periodProgressPercentage(match, now) {
    const duration = durationOfCurrentPeriod(match);
    if (duration === 0) {
        return 0;
    }
    return timeNumericValue(match, now) / duration * 100;
}
/**
 * Utility: Calculate Absolute time based on period and numeric time (and match)
 * @param {Match} match
 * @param {Number} periodNumber
 * @param {Number} numericTime
 * @returns
 */
export function absoluteTime(match, periodNumber, numericTime) {
    let offset = 0;
    // Offset by Past regular periods
    const numberOfRegularPeriodsPast = Math.min(match.config.regularPeriods.quantity + 1, periodNumber) - 1;
    offset += numberOfRegularPeriodsPast > 0
        ? match.config.regularPeriods.duration * numberOfRegularPeriodsPast
        : 0;
    // Offset by past overtime periods
    if (
    // Is past all regular periods, and one overtime periods
    periodNumber > match.config.regularPeriods.quantity + 1
        && match.config.overtimePeriods) {
        const numberOfOvertimePeriodsPastRaw = periodNumber - match.config.regularPeriods.quantity;
        const numberOfOvertimePeriodsPastBoxed = Math.min(match.config.overtimePeriods.quantity + 1, numberOfOvertimePeriodsPastRaw) - 1;
        offset += match.config.overtimePeriods.duration * numberOfOvertimePeriodsPastBoxed;
    }
    return offset + numericTime;
}
//# sourceMappingURL=game-clock.js.map